import { useMemo, useState, type FormEvent } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { getBusinessReport } from '../api/endpoints/reports';
import { ErrorBanner } from '../components/ErrorBanner';
import { FinanzasTabs } from '../components/FinanzasTabs';
import { LoadingState } from '../components/LoadingState';
import { PageHeader } from '../components/PageHeader';
import { PayrollReport } from '../components/PayrollReport';
import { ContadorExport, ReportHeadline } from '../components/ReportHeadline';
import {
  BookingsReport,
  CustomersReport,
  PaymentMethodsReport,
  SalesBreakdown,
  ServicesReport,
  TaxesReport,
  TeamReport,
} from '../components/ReportSections';
import { REPORT_PRESETS, periodFor, periodLabel, type ReportPeriod, type ReportPreset } from '../lib/reports';

type Choice = ReportPreset | 'fechas';

/** The API refuses longer ranges; the picker says so before it asks. */
const MAX_RANGE_DAYS = 366;

/**
 * Finanzas > Reportes: what the owner reads at month end and hands to the
 * contador. One period at a time, always compared with the period before it,
 * every number built with the same rules as Resumen. The Planilla below keeps
 * its own quincena, since coaches are paid twice a month whatever is on top.
 */
export function ReportesPage() {
  const [choice, setChoice] = useState<Choice>('mes');
  const [custom, setCustom] = useState<ReportPeriod>(() => periodFor('mes', new Date()));
  const [draft, setDraft] = useState<ReportPeriod>(custom);
  const [draftError, setDraftError] = useState<string>();

  const period = useMemo(() => (choice === 'fechas' ? custom : periodFor(choice, new Date())), [choice, custom]);
  const reportQuery = useQuery({
    queryKey: ['business-report', period.from, period.to],
    queryFn: () => getBusinessReport(period),
    placeholderData: keepPreviousData,
  });
  const report = reportQuery.data;

  const applyDates = (event: FormEvent) => {
    event.preventDefault();
    const problem = rangeProblem(draft);
    setDraftError(problem);
    if (!problem) setCustom(draft);
  };

  return (
    <main className="report-page">
      <PageHeader title="Finanzas" description="Ingresos, gastos y reportes de tu negocio." />
      <FinanzasTabs />

      <section className="section report-period" aria-label="Período del reporte">
        <div className="chip-row" role="group" aria-label="Período">
          {REPORT_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className="chip"
              data-status={choice === preset.id ? 'active' : undefined}
              onClick={() => setChoice(preset.id)}
            >
              {preset.label}
            </button>
          ))}
          <button
            type="button"
            className="chip"
            data-status={choice === 'fechas' ? 'active' : undefined}
            onClick={() => {
              setDraft(period);
              setChoice('fechas');
            }}
          >
            Otras fechas
          </button>
        </div>
        {choice === 'fechas' ? (
          <form onSubmit={applyDates} aria-label="Rango de fechas" className="filter-row report-dates">
            <div className="field">
              <label className="field-label" htmlFor="reporte-desde">
                Desde
              </label>
              <input
                id="reporte-desde"
                type="date"
                value={draft.from}
                onChange={(event) => setDraft({ ...draft, from: event.target.value })}
              />
            </div>
            <div className="field">
              <label className="field-label" htmlFor="reporte-hasta">
                Hasta
              </label>
              <input
                id="reporte-hasta"
                type="date"
                value={draft.to}
                onChange={(event) => setDraft({ ...draft, to: event.target.value })}
              />
            </div>
            <button type="submit" className="btn small secondary">
              Ver
            </button>
            {draftError ? <p className="field-error">{draftError}</p> : null}
          </form>
        ) : null}
        <p className="report-period-label">
          {periodLabel(period)}
          {reportQuery.isFetching && report ? <span className="row-sub"> · actualizando…</span> : null}
        </p>
      </section>

      {reportQuery.isLoading ? <LoadingState label="Armando el reporte…" /> : null}
      {reportQuery.isError ? (
        <div className="report-error">
          <ErrorBanner message={errorMessage(reportQuery.error)} />
          <button type="button" className="btn small secondary" onClick={() => void reportQuery.refetch()}>
            Reintentar
          </button>
        </div>
      ) : null}

      {report ? (
        <div className="report-body" data-stale={reportQuery.isPlaceholderData || undefined}>
          <ReportHeadline report={report} />
          <SalesBreakdown report={report} />
          <PaymentMethodsReport report={report} />
          <TeamReport report={report} />
          <ServicesReport report={report} />
          <CustomersReport report={report} />
          <BookingsReport report={report} />
          <TaxesReport report={report} />
          <ContadorExport period={period} />
        </div>
      ) : null}

      <PayrollReport />
    </main>
  );
}

function rangeProblem(range: ReportPeriod): string | undefined {
  if (!range.from || !range.to) return 'Elige las dos fechas.';
  if (range.from > range.to) return 'La fecha de inicio va antes que la final.';
  const days = (Date.parse(range.to) - Date.parse(range.from)) / 86_400_000 + 1;
  if (days > MAX_RANGE_DAYS) return 'El reporte cubre hasta un año a la vez.';
  return undefined;
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos armar el reporte. Revisa tu conexión e intenta de nuevo.';
}
